import { useEffect, useRef } from "react";

// Lightweight canvas-based molecular / particle background.
// Blue nodes drifting slowly with subtle connecting lines. Respects prefers-reduced-motion.
export default function MolecularBackground({ dense = false, hero = false }) {
  const ref = useRef(null);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    let raf = 0;
    let w = 0, h = 0, dpr = Math.min(2, window.devicePixelRatio || 1);
    let particles = [];

    const resize = () => {
      w = canvas.clientWidth; h = canvas.clientHeight;
      canvas.width = w * dpr; canvas.height = h * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      const target = Math.min(dense ? 90 : 55, Math.floor((w * h) / 22000));
      particles = new Array(target).fill(0).map(() => {
        const bokeh = Math.random() > 0.9;
        return {
          x: Math.random() * w, y: Math.random() * h,
          vx: (Math.random() - 0.5) * 0.15,
          vy: (Math.random() - 0.5) * 0.15,
          r: bokeh ? Math.random() * 14 + 8 : Math.random() * 1.6 + 0.4,
          bokeh,
        };
      });
    };
    resize();
    window.addEventListener("resize", resize);

    const draw = () => {
      ctx.clearRect(0, 0, w, h);
      for (let i = 0; i < particles.length; i++) {
        const p = particles[i];
        p.x += p.vx; p.y += p.vy;
        if (p.x < -30) p.x = w + 30; if (p.x > w + 30) p.x = -30;
        if (p.y < -30) p.y = h + 30; if (p.y > h + 30) p.y = -30;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        if (p.bokeh) {
          // Soft translucent sphere — echoes the catalog's floating cell/bubble motif
          const g = ctx.createRadialGradient(p.x, p.y, p.r * 0.2, p.x, p.y, p.r);
          g.addColorStop(0, "rgba(56,210,255,0.16)");
          g.addColorStop(0.75, "rgba(30,120,255,0.10)");
          g.addColorStop(1, "rgba(76,157,255,0.28)");
          ctx.fillStyle = g;
          ctx.shadowBlur = 18; ctx.shadowColor = "rgba(56,210,255,0.35)";
        } else {
          ctx.fillStyle = "rgba(76,190,255,0.75)";
          ctx.shadowBlur = 10; ctx.shadowColor = "rgba(56,210,255,0.5)";
        }
        ctx.fill();
      }
      ctx.shadowBlur = 0;

      // connecting lines
      for (let i = 0; i < particles.length; i++) {
        if (particles[i].bokeh) continue;
        for (let j = i + 1; j < particles.length; j++) {
          if (particles[j].bokeh) continue;
          const a = particles[i], b = particles[j];
          const dx = a.x - b.x, dy = a.y - b.y;
          const d2 = dx * dx + dy * dy;
          if (d2 < 130 * 130) {
            const alpha = 1 - Math.sqrt(d2) / 130;
            ctx.strokeStyle = `rgba(76,157,255,${alpha * 0.14})`;
            ctx.lineWidth = 0.6;
            ctx.beginPath(); ctx.moveTo(a.x, a.y); ctx.lineTo(b.x, b.y); ctx.stroke();
          }
        }
      }
      if (!reduce) raf = requestAnimationFrame(draw);
    };
    draw();

    return () => { window.removeEventListener("resize", resize); cancelAnimationFrame(raf); };
  }, [dense]);

  return (
    <div className={`absolute inset-0 pointer-events-none ${hero ? "" : "opacity-70"}`} aria-hidden>
      <canvas ref={ref} className="w-full h-full block" />
    </div>
  );
}
